/* This page is called by <ManagePhotos />
 * <ManageClassPhotos />
 *    <MySelect />
 *    <AddClassPhoto />
 *    <SnackBar />
 *    <ShowClassPhotosToManage />
 *    <EditClassPhoto />
*/

import React, { useEffect, useState }                          from 'react'
import AddClassPhoto                                           from '../Components/AddClassPhoto'
import AddPhotoFieldsComponent                                 from '../Components/AddPhotoFieldsComponent'
import axios                                                   from 'axios'
import Box                                                     from '@material-ui/core/Box'
import ClassDropdown                                           from '../Components/ClassDropdown'
import { CLIENT_PUBLIC_TOKEN }                                 from '../config'
import Container                                               from '@material-ui/core/Container'
import EditClassPhoto                                          from '../Components/EditClassPhoto'
import { getTPSClassesFromNodeServer, node_server }            from '../Helpers'
import Grid                                                    from '@material-ui/core/Grid'
import { Redirect }                                            from 'react-router-dom'
import ShowClassPhotoToManage                                  from '../Components/ShowClassPhotoToManage'
import SnackBar                                                from '../Atoms/SnackBar'
import Typography                                              from '@material-ui/core/Typography'

const ManageClassPhotos = () => {

   const [ activateChildButton,     setActivateChildButton     ] = useState(false)
   const [ redirectToPhotoSearch,   setRedirectToPhotoSearch   ] = useState(false)

   /* Item 1. Get list of TPS Classes from MySQL DB */
   const [ tpsClasses,              setTpsClasses              ] = useState([])
   /* Item 2. TPS class selected via <MySelect /> */
   const [ tpsClass,                setTpsClass                ] = useState('')
   /* Item 3. Get classPhoto from MongoDB */
   const [ classPhoto,              setClassPhoto              ] = useState({})
   /* If a classPhoto exists, display photo and caption */
   const [ showClassPhoto,          setShowClassPhoto          ] = useState(false)
   /* Fetches data from the DB again */
   const [ fetchClassPhoto,         setFetchClassPhoto         ] = useState(false)

   const [ photoIdToEdit,           setPhotoIdToEdit           ] = useState('')
   const [ photoToEdit,             setPhotoToEdit             ] = useState('')
   const [ showEditPhoto,           setShowEditPhoto           ] = useState(false)
   const [ snackBarMsg,             setSnackBarMsg             ] = useState('')
   const [ snackBarOpen,            setSnackBarOpen            ] = useState(false)
   const [ snackBarType,            setSnackBarType            ] = useState('')

   /* 
   * Item 1.
   * This used to call the PHP server and gets a list of all the TPS Classes from tbl_class_page.
   * It now calls the Node Server and does the same thing.
   */
   useEffect( () => {
      getTPSClassesFromNodeServer()
         .then( classes => setTpsClasses(classes) )
         .catch( msg => console.log('There was an error on get', msg) )
   }, [] )

   /* 
   * Item 2.
   * Sent down to <ClassDropdown /> which sends back a String containing the selected TPS Class
   */
   const getSelectedClass = (e) => {
      setTpsClass(e)
   }

   /* 
   * Item 3.
   * Take TPS Class from <MySelect /> and check the MongoDB to see if an entry exists or not
   */
   useEffect( () => {
      if( tpsClass !== '' ){
         axios.defaults.headers.common = {Accept: "application/json, text/plain, */*", 'Authorization': `Bearer ${CLIENT_PUBLIC_TOKEN}`}
         axios.get(node_server + `classPhoto/GetClassPhoto/${tpsClass}`)
               .then( res => res.data )
               .then( data => {
                  if( data ){
                     setClassPhoto( data )
                     setShowClassPhoto(true)
                  }
               } )
      }
   }, [fetchClassPhoto, tpsClass] )

   /*
   * Item 4.
   * Takes updated classPhoto from <AddClassPhoto /> and puts it in local state
   */
   const addClassPhotoToLocalState = (classWithPhotoAdded) => {
      setActivateChildButton(true)
      setClassPhoto(classWithPhotoAdded)
      setShowClassPhoto(true)
   }

   // Updates the classPhoto in MongoDB
   const updateClassPhoto = () => {
      axios.put(node_server + 'classPhoto/UpdateClassPhoto', {
         classPhoto
      })
         .then( res => res.data )
         .then( data => {
               setClassPhoto(data)
               setFetchClassPhoto( !fetchClassPhoto )
         })
   }

   // Sent to <ShowClassPhotoToManage /> to get id of photo to edit
   const getPhotoIdToEdit = (id) => {
      setPhotoIdToEdit(id)
      /* Operating inside a Hook */
      setPhotoToEdit(
         classPhoto.classPhoto.filter( photo => {
               if(photo._id === id){
                  return photo
               }
               return null
         } )
      )
      /* This removes <ShowClassPhotoToManage while EDIT is being performed */
      setShowClassPhoto(false)
      /* This shows or hides <AddClassPhoto /> and <EditClassPhoto /> */
      setShowEditPhoto(true)
   }

   /* Send down to <EditBioPhoto />, will send back updated data */
   const updatedDataForDB = (d) => {
      /* Operating inside a Hook */
      setClassPhoto(
            classPhoto.classPhoto.filter( photo => {
               if(photo._id === photoIdToEdit){
                  photo.classPhotoCaption = d.caption
                  photo.classPhotoWidth = d.classPhotoWidth
                  photo.classPhotoFilename = d.filename
               }
               return null
            } )
      )
      updateClassPhoto()
      setActivateChildButton(true)
      setShowEditPhoto(false)
      setShowClassPhoto(true)
      return null
   }

   // Sent to <ShowClassPhotoToManage /> in order to move a Bio Photo Up One Spot
   const movePhotoEntryUp = (index) => {
      const tempClassPhoto = classPhoto.classPhoto[index - 1]
      classPhoto.classPhoto[index - 1] = classPhoto.classPhoto[index]
      classPhoto.classPhoto[index] = tempClassPhoto

      updateClassPhoto()
   }

   // Sent to <ShowClassPhotoToManage /> in order to move a Bio Photo Down One Spot
   const movePhotoEntryDown = (index) => {
      const tempClassPhoto = classPhoto.classPhoto[index + 1]
      classPhoto.classPhoto[index + 1] = classPhoto.classPhoto[index]
      classPhoto.classPhoto[index] = tempClassPhoto

      updateClassPhoto()
   }

   const handleSnackBar = ({msg, type}) => {
      setSnackBarMsg(msg)
      setSnackBarType(type)
      setSnackBarOpen(true)
      setTimeout(() => {
         setSnackBarMsg('')
         setSnackBarType('')
         setSnackBarOpen(false)
         setRedirectToPhotoSearch(true)
     }, 2500)
   }

   return (
      <Box style={{marginTop: '6rem'}} >

         { redirectToPhotoSearch ? <Redirect to='/PhotoSearch' /> : null }

         <Typography variant='h4' >
               Manage Class Photos
         </Typography>

         <Container>
               <ClassDropdown tpsClasses={tpsClasses} getSelectedClass={getSelectedClass} />
         </Container>

         {showEditPhoto ? null : <AddClassPhoto addClassPhotoToLocalState={addClassPhotoToLocalState} classPhoto={classPhoto} tpsClass={tpsClass} />}

         {showClassPhoto
            ?
               <ShowClassPhotoToManage classPhoto={classPhoto} getPhotoIdToEdit={getPhotoIdToEdit} movePhotoEntryDown={movePhotoEntryDown} movePhotoEntryUp={movePhotoEntryUp} />
            :
               null
            }

         {showEditPhoto ? <EditClassPhoto photoToEdit={photoToEdit} updatedDataForDB={updatedDataForDB} /> : null}

         {snackBarOpen
            ?
               <Grid container justifyContent="center" style={{ marginTop: "2rem" }}>
                  <Grid item sm={6}>
                        <SnackBar msg={snackBarMsg} type={snackBarType} />
                  </Grid>
               </Grid>
            :
            null
         }

         <AddPhotoFieldsComponent
            activateChildButton={activateChildButton}
            handleSnackBar={handleSnackBar}
            title={'Class Photos - Add Photo Page'}
         />

      </Box>
   )
}

export default ManageClassPhotos